import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../styles/theme';

const PanelOverlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: ${theme.zIndex.modal - 1};
  backdrop-filter: blur(2px);
`;

const PanelContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(420px, 100vw);
  background: ${theme.colors.background.secondary};
  border-left: 1px solid ${theme.colors.border.primary};
  z-index: ${theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.3);
`;

const PanelHeader = styled.div`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`;

const PanelTitle = styled.h2`
  margin: 0;
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.secondary};
  font-size: 1.5rem;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  &:hover {
    color: ${theme.colors.text.primary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const VesselSelector = styled.select`
  margin: 0 ${theme.spacing.lg} ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.input.background};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  flex-shrink: 0;
`;

const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const MessageRow = styled.div`
  display: flex;
  justify-content: ${(p) => (p.$isUser ? 'flex-end' : 'flex-start')};
  width: 100%;
  flex-shrink: 0;
  min-width: 0;
`;

const MessageBubble = styled.div`
  max-width: 90%;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  background: ${(p) =>
    p.$isUser ? theme.colors.primary.main : theme.colors.background.card};
  color: ${(p) => (p.$isUser ? theme.colors.primary.contrast : theme.colors.text.primary)};
  border: ${(p) => (p.$isUser ? 'none' : `1px solid ${theme.colors.border.primary}`)};
  position: relative;
  z-index: 1;
`;

const MessageTime = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${(p) => (p.$isUser ? 'rgba(255,255,255,0.7)' : theme.colors.text.disabled)};
  margin-top: 4px;
  display: block;
`;

const InputArea = styled.div`
  padding: ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.border.primary};
  flex-shrink: 0;
`;

const InputRow = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-end;
`;

const Input = styled.textarea`
  flex: 1;
  min-height: 44px;
  max-height: 120px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.input.background};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  resize: none;
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.light};
  }
  &::placeholder {
    color: ${theme.colors.text.disabled};
  }
`;

const SendButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.primary.main} 0%, ${theme.colors.primary.dark} 100%);
  color: ${theme.colors.primary.contrast};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  cursor: pointer;
  flex-shrink: 0;
  &:hover:not(:disabled) {
    opacity: 0.95;
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  text-align: center;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
`;

const TabsStrip = styled.div`
  display: flex;
  gap: 2px;
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius.md};
  padding: 2px;
  margin: 0 ${theme.spacing.lg} ${theme.spacing.md};
  flex-shrink: 0;
`;

const Tab = styled.button`
  flex: 1;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${(p) => (p.$active ? theme.colors.primary.main : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.primary.contrast : theme.colors.text.secondary)};
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${(p) => (p.$active ? theme.typography.fontWeight.semibold : theme.typography.fontWeight.medium)};
  cursor: pointer;
  &:hover:not([disabled]) {
    color: ${(p) => (p.$active ? theme.colors.primary.contrast : theme.colors.text.primary)};
    background: ${(p) => (p.$active ? theme.colors.primary.main : 'rgba(255,255,255,0.06)')};
  }
`;

const ErrorBanner = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid ${theme.colors.error.main};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.error.light};
  font-size: ${theme.typography.fontSize.sm};
  margin: 0 ${theme.spacing.lg} ${theme.spacing.md};
`;

const AIAssistantPanel = ({ isOpen, onClose, initialMessage, onInitialMessageConsumed }) => {
  const { isAuthenticated } = useAuth();
  const [vessels, setVessels] = useState([]);
  const [selectedVesselIds, setSelectedVesselIds] = useState(null); // null = all vessels
  const [conversations, setConversations] = useState([]);
  const [mode, setMode] = useState('platform'); // 'platform' | 'expert'
  const [platformConversation, setPlatformConversation] = useState(null);
  const [expertConversation, setExpertConversation] = useState(null);
  const [platformMessages, setPlatformMessages] = useState([]);
  const [expertMessages, setExpertMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const messages = mode === 'platform' ? platformMessages : expertMessages;
  const setMessages = mode === 'platform' ? setPlatformMessages : setExpertMessages;
  const currentConversation = mode === 'platform' ? platformConversation : expertConversation;

  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    setStatusLoaded(false);
    const load = async () => {
      try {
        const [vRes, sRes] = await Promise.all([
          api.get('/api/vessels'),
          api.get('/api/ai-assistant/status'),
        ]);
        const vList = vRes.data?.vessels || [];
        setVessels(vList);
        setConfigured(sRes.data?.configured ?? false);
      } catch (e) {
        setError('Could not load vessels');
      } finally {
        setStatusLoaded(true);
      }
    };
    load();
  }, [isOpen, isAuthenticated]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return;
    inputRef.current?.focus();
  }, [isOpen]);

  const sendMessage = async (text) => {
    const msg = String(text).trim();
    if (!msg || loading || !configured) return false;

    setInput('');
    setLoading(true);
    setError(null);

    const userMsg = {
      role: 'user',
      content: msg,
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);

    try {
      const res = await api.post('/api/ai-assistant/chat', {
        message: msg,
        conversationId: currentConversation?.id || null,
        vesselIds: mode === 'platform' ? selectedVesselIds : null,
        mode,
      });

      const assistantMsg = {
        role: 'assistant',
        content: res.data.content,
        createdAt: new Date().toISOString(),
      };
      setMessages((m) => [...m, assistantMsg]);

      if (res.data.conversationId && !currentConversation) {
        if (mode === 'platform') {
          setPlatformConversation({ id: res.data.conversationId });
        } else {
          setExpertConversation({ id: res.data.conversationId });
        }
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send message');
      setMessages((m) => m.filter((item) => item.role !== 'user' || item.content !== msg));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    await sendMessage(input.trim());
  };

  useEffect(() => {
    if (!isOpen || !initialMessage || !configured || loading) return;
    const msg = String(initialMessage).trim();
    if (!msg) {
      onInitialMessageConsumed?.();
      return;
    }
    onInitialMessageConsumed?.();
    sendMessage(msg);
  }, [isOpen, initialMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isAuthenticated) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <PanelOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <PanelContainer
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          >
            <PanelHeader>
              <PanelTitle>
                <span role="img" aria-label="boat">
                  🚢
                </span>
                Talk to your boat
              </PanelTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                ×
              </CloseButton>
            </PanelHeader>

            <TabsStrip>
              <Tab
                $active={mode === 'platform'}
                onClick={() => setMode('platform')}
                aria-pressed={mode === 'platform'}
              >
                My Boat
              </Tab>
              <Tab
                $active={mode === 'expert'}
                onClick={() => setMode('expert')}
                aria-pressed={mode === 'expert'}
              >
                BoatWise Expert
              </Tab>
            </TabsStrip>

            {mode === 'platform' && vessels.length > 1 && (
              <VesselSelector
                value={selectedVesselIds === null ? 'all' : String(selectedVesselIds)}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedVesselIds(v === 'all' ? null : [parseInt(v, 10)]);
                }}
              >
                <option value="all">All my boats</option>
                {vessels.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.vessel_name}
                  </option>
                ))}
              </VesselSelector>
            )}

            {statusLoaded && !configured && (
              <ErrorBanner>
                AI assistant is not configured. Set OPENROUTER_API_KEY or OPENAI_API_KEY on the server to enable.
              </ErrorBanner>
            )}

            {error && <ErrorBanner>{error}</ErrorBanner>}

            <MessagesArea>
              {messages.length === 0 && (
                <EmptyState>
                  {mode === 'platform' ? (
                    <>
                      <p>Ask anything about your boat(s).</p>
                      <p style={{ marginTop: 8, fontSize: '0.8rem' }}>
                        e.g. &quot;What&apos;s my battery voltage?&quot; or &quot;How was my last trip?&quot;
                      </p>
                    </>
                  ) : (
                    <>
                      <p>Ask about equipment, manuals, troubleshooting.</p>
                      <p style={{ marginTop: 8, fontSize: '0.8rem' }}>
                        e.g. &quot;How do I calibrate my depth sounder?&quot; or &quot;Raymarine autopilot setup&quot;
                      </p>
                    </>
                  )}
                </EmptyState>
              )}
              {messages.map((msg, i) => (
                <MessageRow key={i} $isUser={msg.role === 'user'}>
                  <MessageBubble $isUser={msg.role === 'user'}>
                    {msg.content}
                    <MessageTime $isUser={msg.role === 'user'}>
                      {new Date(msg.createdAt).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </MessageTime>
                  </MessageBubble>
                </MessageRow>
              ))}
              {loading && (
                <MessageRow $isUser={false}>
                  <MessageBubble $isUser={false}>
                    <em>Thinking…</em>
                  </MessageBubble>
                </MessageRow>
              )}
              <div ref={messagesEndRef} />
            </MessagesArea>

            <InputArea>
              <InputRow>
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={mode === 'platform' ? 'Ask about your boat...' : 'Ask about equipment, manuals...'}
                  disabled={!configured || loading}
                  rows={1}
                />
                <SendButton
                  onClick={handleSend}
                  disabled={!input.trim() || loading || !configured}
                >
                  Send
                </SendButton>
              </InputRow>
            </InputArea>
          </PanelContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default AIAssistantPanel;
